import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function PdfProtect() {
  const { t } = useTranslation();
  const [mode, setMode] = useState("set"); // set | remove
  const [file, setFile] = useState(null);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function run() {
    if (!file || !pwd) {
      toast.error(t("protect_password"));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("password", pwd);
      const url = mode === "set" ? "/pdf/protect" : "/pdf/unlock";
      const { data } = await api.post(url, fd);
      setResult(data);
      toast.success(t("success"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-12" data-testid="pdf-protect-page">
      <Link to="/tools/pdf" className="eyebrow text-zinc-500 hover:text-black">← {t("cta_back")}</Link>
      <h1 className="hero-h2 text-3xl sm:text-5xl mt-2">{t("tool_protect")}</h1>

      <div className="mt-8">
        <div className="flex border border-zinc-200 rounded-sm w-fit overflow-hidden mb-6">
          <button className={`px-4 py-2 text-xs uppercase tracking-wider ${mode === "set" ? "bg-black text-white" : "bg-white text-zinc-700"}`}
            onClick={() => { setMode("set"); setResult(null); }} data-testid="protect-mode-set">
            {t("protect_set")}
          </button>
          <button className={`px-4 py-2 text-xs uppercase tracking-wider ${mode === "remove" ? "bg-black text-white" : "bg-white text-zinc-700"}`}
            onClick={() => { setMode("remove"); setResult(null); }} data-testid="protect-mode-remove">
            {t("protect_remove")}
          </button>
        </div>

        {!file ? (
          <DropZone accept="application/pdf" onFiles={(fs) => setFile(fs[0])} testId="protect-dropzone" />
        ) : (
          <div className="border border-zinc-200 rounded-md p-6 bg-white space-y-5">
            <div className="hero-h2 text-xl">{file.name}</div>
            <div className="max-w-sm">
              <Label className="eyebrow">{t("protect_password")}</Label>
              <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                className="rounded-sm border-zinc-300 h-11 mt-1" data-testid="protect-password" />
            </div>
            <div className="flex gap-3">
              <Button onClick={run} disabled={busy}
                className="rounded-sm bg-black text-white hover:bg-zinc-800 h-11" data-testid="protect-run">
                {busy ? t("processing") : `${t("cta_apply")} →`}
              </Button>
              <Button variant="ghost" className="rounded-sm h-11" onClick={() => { setFile(null); setResult(null); setPwd(""); }}>
                {t("cta_clear")}
              </Button>
            </div>
          </div>
        )}
        {result && (
          <div className="mt-6 border border-black rounded-md p-6 bg-zinc-50 fade-up" data-testid="protect-result">
            <span className="eyebrow">{t("result_ready")}</span>
            <div className="hero-h2 text-xl mt-1">{result.original_filename}</div>
            <Button onClick={() => downloadFile(result.id, result.original_filename)}
              className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 mt-4">
              ↓ {t("cta_download")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
